/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AmountString,
  Amounts,
  HostPortPath,
  IbanString,
  PaytoString,
  PaytoType,
  Paytos,
  TalerCorebankApi,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  CopyButton,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { useSessionState } from "../../hooks/session.js";
import {
  ErrorMessageMappingFor,
  TanChannel,
  undefinedIfEmpty,
  validateIBAN,
  validateTalerBank,
} from "../../utils.js";
import {
  InputAmount,
  TextField,
  doAutoFocus,
} from "../PaytoWireTransferForm.js";
import { getRandomPassword } from "../rnd.js";

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const REGEX_JUST_NUMBERS_REGEX = /^\+[0-9 ]*$/;

export type AccountFormData = {
  debit_threshold?: string;
  isExchange?: boolean;
  isPublic?: boolean;
  name?: string;
  username?: string;
  payto_uri?: string;
  cashout_payto_uri?: string;
  email?: string;
  phone?: string;
  tan_channels?: TanChannel[];
};

export function buildAccountReconfiguration(
  changed: AccountFormData,
  cashoutPaytoUri: PaytoString | null,
  debitThreshold: AmountString | undefined,
): TalerCorebankApi.AccountReconfiguration {
  const result: TalerCorebankApi.AccountReconfiguration = {};
  if (changed.cashout_payto_uri !== undefined) {
    result.cashout_payto_uri = cashoutPaytoUri;
  }
  const contactData: TalerCorebankApi.ChallengeContactDataPatch = {};
  if (changed.email !== undefined) {
    contactData.email = changed.email.trim() || null;
  }
  if (changed.phone !== undefined) {
    contactData.phone = changed.phone.trim() || null;
  }
  const contactDataPatch = undefinedIfEmpty(contactData);
  if (contactDataPatch !== undefined) {
    result.contact_data = contactDataPatch;
  }
  if (changed.debit_threshold !== undefined) {
    result.debit_threshold = debitThreshold;
  }
  if (changed.isPublic !== undefined) {
    result.is_public = changed.isPublic;
  }
  if (changed.name !== undefined) {
    result.name = changed.name;
  }
  if (changed.tan_channels !== undefined) {
    result.tan_channels = changed.tan_channels;
  }
  return result;
}

type ChangeByPurposeType = {
  create: (a: TalerCorebankApi.RegisterAccountRequest | undefined) => void;
  update: (a: TalerCorebankApi.AccountReconfiguration | undefined) => void;
  show: undefined;
};
/**
 * FIXME:
 * is_public is missing on PATCH
 * account email/password should require 2FA
 *
 *
 * @param param0
 * @returns
 */
export function AccountForm<PurposeType extends keyof ChangeByPurposeType>({
  template,
  username,
  purpose,
  onChange,
  focus,
  hideNonEditableFields = false,
  children,
}: {
  focus?: boolean;
  hideNonEditableFields?: boolean;
  children: ComponentChildren;
  username?: string;
  template: TalerCorebankApi.AccountData | undefined;
  onChange: ChangeByPurposeType[PurposeType];
  purpose: PurposeType;
}): VNode {
  const { config, url } = useBankCoreApiContext();
  const { i18n } = useTranslationContext();
  const { state: credentials } = useSessionState();
  const [form, setForm] = useState<AccountFormData>({});
  const [channelNotice, setChannelNotice] = useState<string>();

  const [errors, setErrors] = useState<
    ErrorMessageMappingFor<typeof defaultValue> | undefined
  >(undefined);

  const paytoType =
    config.wire_type === "X_TALER_BANK"
      ? ("x-taler-bank" as const)
      : ("iban" as const);
  const cashoutPaytoType: typeof paytoType = "iban" as const;

  const defaultValue: AccountFormData = {
    debit_threshold: Amounts.stringifyValue(
      template?.debit_threshold ??
        config.default_debit_threshold ??
        `${config.currency}:0`,
    ),
    isExchange: template?.is_taler_exchange,
    isPublic: template?.is_public,
    name: template?.name ?? "",
    cashout_payto_uri:
      getAccountId(
        cashoutPaytoType,
        template?.cashout_payto_uri as Paytos.FullPaytoString,
      ) ?? "",
    payto_uri:
      getAccountId(paytoType, template?.payto_uri as Paytos.FullPaytoString) ??
      "",
    email: template?.contact_data?.email ?? "",
    phone: template?.contact_data?.phone ?? "",
    username: username ?? "",
    tan_channels: template?.tan_channels ?? [],
  };

  const userIsAdmin =
    credentials.status !== "loggedIn" ? false : credentials.isUserAdministrator;

  const editableUsername = purpose === "create";
  const editableName =
    purpose === "create" ||
    (purpose === "update" && (config.allow_edit_name || userIsAdmin));

  const isCashoutEnabled = config.allow_conversion;
  const editableCashout =
    purpose === "create" ||
    (purpose === "update" &&
      (config.allow_edit_cashout_payto_uri || userIsAdmin));
  const editableThreshold =
    userIsAdmin && (purpose === "create" || purpose === "update");
  const editableAccount = purpose === "create" && userIsAdmin;

  const effectiveForm = { ...defaultValue, ...form };
  const hasPhone = !!effectiveForm.phone?.trim();
  const hasEmail = !!effectiveForm.email?.trim();

  function updateForm(newForm: typeof defaultValue): void {
    const effective = { ...defaultValue, ...newForm };
    const disabledChannels: TanChannel[] = [];
    let effectiveChannels = effective.tan_channels ?? [];
    if (
      newForm.email !== undefined &&
      !newForm.email.trim() &&
      effectiveChannels.includes(TanChannel.EMAIL)
    ) {
      effectiveChannels = effectiveChannels.filter(
        (channel) => channel !== TanChannel.EMAIL,
      );
      disabledChannels.push(TanChannel.EMAIL);
    }
    if (
      newForm.phone !== undefined &&
      !newForm.phone.trim() &&
      effectiveChannels.includes(TanChannel.SMS)
    ) {
      effectiveChannels = effectiveChannels.filter(
        (channel) => channel !== TanChannel.SMS,
      );
      disabledChannels.push(TanChannel.SMS);
    }
    if (disabledChannels.length > 0) {
      newForm = { ...newForm, tan_channels: effectiveChannels };
      effective.tan_channels = effectiveChannels;
      setChannelNotice(
        disabledChannels.includes(TanChannel.EMAIL) &&
          disabledChannels.includes(TanChannel.SMS)
          ? i18n.str`Email and SMS authentication were disabled because their contact details were removed.`
          : disabledChannels.includes(TanChannel.EMAIL)
            ? i18n.str`Email authentication was disabled because the email address was removed.`
            : i18n.str`SMS authentication was disabled because the phone number was removed.`,
      );
    } else {
      setChannelNotice(undefined);
    }

    const trimmedDebitThresholdStr = effective.debit_threshold?.trim();
    const parsedDebitThreshold = Amounts.parse(
      `${config.currency}:${trimmedDebitThresholdStr}`,
    );

    const errors = undefinedIfEmpty<
      ErrorMessageMappingFor<typeof defaultValue>
    >({
      cashout_payto_uri: !effective.cashout_payto_uri
        ? undefined
        : !editableCashout
          ? undefined
          : !effective.cashout_payto_uri
            ? undefined
            : cashoutPaytoType === "iban"
              ? validateIBAN(effective.cashout_payto_uri, i18n)
              : cashoutPaytoType === "x-taler-bank"
                ? validateTalerBank(effective.cashout_payto_uri, i18n)
                : undefined,

      payto_uri: !effective.payto_uri
        ? undefined
        : !editableAccount
          ? undefined
          : !effective.payto_uri
            ? undefined
            : paytoType === "iban"
              ? validateIBAN(effective.payto_uri, i18n)
              : paytoType === "x-taler-bank"
                ? validateTalerBank(effective.payto_uri, i18n)
                : undefined,

      email: !effective.email
        ? undefined
        : !EMAIL_REGEX.test(effective.email)
          ? i18n.str`Invalid email format`
          : undefined,
      phone: !effective.phone
        ? undefined
        : !effective.phone.startsWith("+") // FIXME: better phone number check
          ? i18n.str`Should start with +`
          : !REGEX_JUST_NUMBERS_REGEX.test(effective.phone)
            ? i18n.str`A phone number consists of numbers only`
            : undefined,
      debit_threshold: !editableThreshold
        ? undefined
        : !trimmedDebitThresholdStr
          ? undefined
          : !parsedDebitThreshold
            ? i18n.str`Not valid`
            : undefined,
      name: !editableName
        ? undefined // disabled
        : purpose === "update" && newForm.name === undefined
          ? undefined // the field hasn't been changed
          : !effective.name
            ? i18n.str`Required`
            : undefined,
      username: !editableUsername
        ? undefined
        : !effective.username
          ? i18n.str`Required`
          : undefined,
    });
    setErrors(errors);

    setForm(newForm);
    if (!onChange) return;

    if (errors) {
      onChange(undefined);
    } else {
      let cashout: Paytos.URI | undefined;
      if (effective.cashout_payto_uri)
        switch (cashoutPaytoType) {
          case "x-taler-bank": {
            cashout = Paytos.createTalerBank(
              url.href as HostPortPath,
              effective.cashout_payto_uri,
            );
            break;
          }
          case "iban": {
            cashout = Paytos.createIban(
              effective.cashout_payto_uri as IbanString,
              undefined,
            );
            break;
          }
          default:
            assertUnreachable(cashoutPaytoType);
        }
      const cashoutURI = !cashout ? null : Paytos.toFullString(cashout);
      let internal: Paytos.URI | undefined;
      if (effective.payto_uri)
        switch (paytoType) {
          case "x-taler-bank": {
            internal = Paytos.createTalerBank(
              url.href as HostPortPath,
              effective.payto_uri,
            );
            break;
          }
          case "iban": {
            internal = Paytos.createIban(
              effective.payto_uri as IbanString,
              undefined,
            );
            break;
          }
          default:
            assertUnreachable(paytoType);
        }
      const internalURI = !internal ? undefined : Paytos.toFullString(internal);

      const threshold = !parsedDebitThreshold
        ? undefined
        : Amounts.stringify(parsedDebitThreshold);

      switch (purpose) {
        case "create": {
          // typescript doesn't correctly narrow a generic type
          const callback = onChange as ChangeByPurposeType["create"];
          const result: TalerCorebankApi.RegisterAccountRequest = {
            name: effective.name!,
            password: getRandomPassword(),
            username: effective.username!,
            contact_data: undefinedIfEmpty({
              email: !effective.email ? undefined : effective.email,
              phone: !effective.phone ? undefined : effective.phone,
            }),
            debit_threshold: threshold ?? config.default_debit_threshold,
            cashout_payto_uri: cashoutURI === null ? undefined : cashoutURI,
            payto_uri: internalURI,
            is_public: effective.isPublic,
            is_taler_exchange: effective.isExchange,
            tan_channels: effective.tan_channels?.length
              ? effective.tan_channels
              : undefined,
          };
          callback(result);
          return;
        }
        case "update": {
          // typescript doesn't correctly narrow a generic type
          const callback = onChange as ChangeByPurposeType["update"];

          const result = buildAccountReconfiguration(
            newForm,
            cashoutURI,
            threshold,
          );
          callback(result);
          return;
        }
        case "show": {
          return;
        }
        default: {
          assertUnreachable(purpose);
        }
      }
    }
  }

  function toggleTanChannel(channel: TanChannel): void {
    const channels = effectiveForm.tan_channels ?? [];
    updateForm({
      ...form,
      tan_channels: channels.includes(channel)
        ? channels.filter((candidate) => candidate !== channel)
        : [...channels, channel],
    });
  }
  return (
    <form
      class="bg-white shadow-sm ring-1 ring-onBackground/5 sm:rounded-xl md:col-span-2"
      autoCapitalize="none"
      autoCorrect="off"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div class="px-4 py-6 sm:p-8">
        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          {hideNonEditableFields && !editableUsername ? undefined : (
            <div class="sm:col-span-5">
              <label
                class="block text-sm font-medium leading-6 text-onBackground"
                for="username"
              >
                {i18n.str`Login username`}
                {editableUsername && <b class="text-[red]"> *</b>}
              </label>
              <div class="mt-2">
                <input
                  ref={focus && purpose === "create" ? doAutoFocus : undefined}
                  type="text"
                  class="block w-full disabled:bg-gray-100 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  name="username"
                  id="username"
                  data-error={!!errors?.username && form.username !== undefined}
                  disabled={!editableUsername}
                  value={form.username ?? defaultValue.username}
                  onChange={(e) => {
                    form.username = e.currentTarget.value;
                    updateForm(structuredClone(form));
                  }}
                  // placeholder=""
                  autocomplete="off"
                />
                <ShowInputErrorLabel
                  message={errors?.username}
                  isDirty={form.username !== undefined}
                />
              </div>
              <p class="mt-2 text-sm text-gray-500">
                <i18n.Translate>Account ID for authentication</i18n.Translate>
              </p>
            </div>
          )}

          {hideNonEditableFields && !editableName ? undefined : (
            <div class="sm:col-span-5">
              <label
                class="block text-sm font-medium leading-6 text-onBackground"
                for="name"
              >
                {i18n.str`Full name`}
                {editableName && <b class="text-[red]"> *</b>}
              </label>
              <div class="mt-2">
                <input
                  type="text"
                  class="block w-full disabled:bg-gray-100 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  name="name"
                  data-error={!!errors?.name && form.name !== undefined}
                  id="name"
                  disabled={!editableName}
                  value={form.name ?? defaultValue.name}
                  onChange={(e) => {
                    form.name = e.currentTarget.value;
                    updateForm(structuredClone(form));
                  }}
                  // placeholder=""
                  autocomplete="off"
                />
                <ShowInputErrorLabel
                  message={errors?.name}
                  isDirty={form.name !== undefined}
                />
              </div>
              <p class="mt-2 text-sm text-gray-500">
                <i18n.Translate>Name of the account holder</i18n.Translate>
              </p>
            </div>
          )}

          {purpose === "create" ||
          (hideNonEditableFields && !editableAccount) ? undefined : (
            <TextField
              id="internal-account"
              label={i18n.str`Internal account`}
              help={i18n.str`Copy and share this account address to receive transfers.`}
              error={errors?.payto_uri}
              onChange={(e) => {
                form.payto_uri = e as PaytoString;
                updateForm(structuredClone(form));
              }}
              rightIcons={
                <CopyButton
                  class="p-2 rounded-full  text-onBackground shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 "
                  getContent={() =>
                    form.payto_uri ?? defaultValue.payto_uri ?? ""
                  }
                />
              }
              value={(form.payto_uri ?? defaultValue.payto_uri) as PaytoString}
              disabled={!editableAccount}
            />
          )}

          <div class="sm:col-span-5">
            <label
              class="block text-sm font-medium leading-6 text-onBackground"
              for="email"
            >
              {i18n.str`Email`}
            </label>
            <div class="mt-2">
              <input
                type="email"
                class="block w-full disabled:bg-gray-100 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                name="email"
                id="email"
                data-error={!!errors?.email && form.email !== undefined}
                disabled={purpose === "show"}
                value={form.email ?? defaultValue.email}
                onChange={(e) => {
                  form.email = e.currentTarget.value;
                  updateForm(structuredClone(form));
                }}
                autocomplete="off"
              />
              <ShowInputErrorLabel
                message={errors?.email}
                isDirty={form.email !== undefined}
              />
            </div>
            <p class="mt-2 text-sm text-gray-500">
              <i18n.Translate>
                To be used when second factor authentication is enabled
              </i18n.Translate>
            </p>
          </div>

          <div class="sm:col-span-5">
            <label
              class="block text-sm font-medium leading-6 text-onBackground"
              for="phone"
            >
              {i18n.str`Phone`}
            </label>
            <div class="mt-2">
              <input
                type="text"
                class="block w-full disabled:bg-gray-100 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                name="phone"
                id="phone"
                disabled={purpose === "show"}
                value={form.phone ?? defaultValue.phone}
                data-error={!!errors?.phone && form.phone !== undefined}
                onChange={(e) => {
                  form.phone = e.currentTarget.value;
                  updateForm(structuredClone(form));
                }}
                autocomplete="off"
              />
              <ShowInputErrorLabel
                message={errors?.phone}
                isDirty={form.phone !== undefined}
              />
            </div>
            <p class="mt-2 text-sm text-gray-500">
              <i18n.Translate>
                To be used when second factor authentication is enabled
              </i18n.Translate>
            </p>
          </div>

          {!config.supported_tan_channels ||
          config.supported_tan_channels.length === 0 ? undefined : (
            <div class="sm:col-span-5">
              <label
                class="block text-sm font-medium leading-6 text-onBackground"
                for="channel"
              >
                {i18n.str`Enable second factor authentication`}
              </label>
              {channelNotice && (
                <p
                  class="mt-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900"
                  role="status"
                >
                  {channelNotice}
                </p>
              )}
              <div class="mt-2 max-w-xl text-sm text-gray-500">
                <div class="px-4 mt-4 grid grid-cols-1 gap-y-6">
                  {config.supported_tan_channels.indexOf(TanChannel.EMAIL) ===
                  -1 ? undefined : (
                    <label
                      aria-label={i18n.str`Using email`}
                      data-disabled={purpose === "show" || !hasEmail}
                      data-selected={effectiveForm.tan_channels?.includes(
                        TanChannel.EMAIL,
                      )}
                      class="relative flex data-[disabled=false]:cursor-pointer rounded-lg border bg-white data-[disabled=true]:bg-gray-200 p-4 shadow-sm focus:outline-none border-gray-300 data-[selected=true]:ring-2 data-[selected=true]:ring-primary"
                    >
                      <input
                        type="checkbox"
                        name="email-channel"
                        value={TanChannel.EMAIL}
                        disabled={purpose === "show" || !hasEmail}
                        aria-labelledby="tan-email-label"
                        checked={effectiveForm.tan_channels?.includes(
                          TanChannel.EMAIL,
                        )}
                        onChange={() => toggleTanChannel(TanChannel.EMAIL)}
                        class="sr-only"
                      />
                      <span class="flex flex-1">
                        <span class="flex flex-col">
                          <span
                            id="tan-email-label"
                            class="block text-sm font-medium text-onBackground "
                          >
                            <i18n.Translate>Using email</i18n.Translate>
                          </span>
                          {purpose !== "show" &&
                            !hasEmail &&
                            i18n.str`Add an email in your profile to enable this option`}
                        </span>
                      </span>
                      <svg
                        data-selected={effectiveForm.tan_channels?.includes(
                          TanChannel.EMAIL,
                        )}
                        class="h-5 w-5 text-primary data-[selected=false]:hidden"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </label>
                  )}

                  {config.supported_tan_channels.indexOf(TanChannel.SMS) ===
                  -1 ? undefined : (
                    <label
                      aria-label={i18n.str`Using SMS`}
                      data-disabled={purpose === "show" || !hasPhone}
                      data-selected={effectiveForm.tan_channels?.includes(
                        TanChannel.SMS,
                      )}
                      class="relative flex data-[disabled=false]:cursor-pointer rounded-lg border data-[disabled=true]:bg-gray-200 p-4 shadow-sm focus:outline-none border-gray-300 data-[selected=true]:ring-2 data-[selected=true]:ring-primary"
                    >
                      <input
                        type="checkbox"
                        name="sms-channel"
                        value={TanChannel.SMS}
                        disabled={purpose === "show" || !hasPhone}
                        aria-labelledby="tan-sms-label"
                        checked={effectiveForm.tan_channels?.includes(
                          TanChannel.SMS,
                        )}
                        onChange={() => toggleTanChannel(TanChannel.SMS)}
                        class="sr-only"
                      />
                      <span class="flex flex-1">
                        <span class="flex flex-col">
                          <span
                            id="tan-sms-label"
                            class="block text-sm font-medium text-onBackground"
                          >
                            <i18n.Translate>Using SMS</i18n.Translate>
                          </span>
                          {purpose !== "show" &&
                            !hasPhone &&
                            i18n.str`Add a phone number in your profile to enable this option`}
                        </span>
                      </span>
                      <svg
                        data-selected={effectiveForm.tan_channels?.includes(
                          TanChannel.SMS,
                        )}
                        class="h-5 w-5 text-primary data-[selected=false]:hidden"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {isCashoutEnabled && (!hideNonEditableFields || editableCashout) && (
            <TextField
              id="cashout-account"
              label={i18n.str`Cashout account`}
              help={i18n.str`External account to which cashout transfers are sent.`}
              error={errors?.cashout_payto_uri}
              onChange={(e) => {
                form.cashout_payto_uri = e as PaytoString;
                updateForm(structuredClone(form));
              }}
              value={
                (form.cashout_payto_uri ??
                  defaultValue.cashout_payto_uri) as PaytoString
              }
              disabled={!editableCashout}
            />
          )}

          {hideNonEditableFields && !editableThreshold ? undefined : (
            <div class="sm:col-span-5">
              <label
                for="debit"
                class="block text-sm font-medium leading-6 text-onBackground"
              >{i18n.str`Max debt`}</label>
              <InputAmount
                name="debit"
                left
                currency={config.currency}
                value={form.debit_threshold ?? defaultValue.debit_threshold}
                onChange={
                  !editableThreshold
                    ? undefined
                    : (e) => {
                        form.debit_threshold = e as AmountString;
                        updateForm(structuredClone(form));
                      }
                }
              />
              <ShowInputErrorLabel
                message={
                  errors?.debit_threshold
                    ? String(errors?.debit_threshold)
                    : undefined
                }
                isDirty={form.debit_threshold !== undefined}
              />
              <p class="mt-2 text-sm text-gray-500">
                <i18n.Translate>
                  How much the balance can go below zero.
                </i18n.Translate>
              </p>
            </div>
          )}

          <div class="sm:col-span-5">
            <div class="flex items-center justify-between">
              <span class="flex flex-grow flex-col">
                <span
                  class="text-sm text-onBackground font-medium leading-6 "
                  id="public-account-label"
                >
                  <i18n.Translate>Is this account public?</i18n.Translate>
                </span>
              </span>
              <button
                type="button"
                disabled={purpose === "show"}
                name="is public"
                data-enabled={
                  (form.isPublic ?? defaultValue.isPublic) ? "true" : "false"
                }
                class="bg-primary data-[enabled=false]:bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                role="switch"
                aria-checked={!!(form.isPublic ?? defaultValue.isPublic)}
                aria-labelledby="public-account-label"
                aria-describedby="public-account-description"
                onClick={() => {
                  if (purpose === "show") return;
                  form.isPublic = !(form.isPublic ?? defaultValue.isPublic);
                  updateForm(structuredClone(form));
                }}
              >
                <span
                  aria-hidden="true"
                  data-enabled={
                    (form.isPublic ?? defaultValue.isPublic) ? "true" : "false"
                  }
                  class="translate-x-5 data-[enabled=false]:translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                ></span>
              </button>
            </div>
            <p
              id="public-account-description"
              class="mt-2 text-sm text-gray-500"
            >
              <i18n.Translate>
                Public accounts expose their balance and complete transaction
                history, including subjects and both account addresses, to
                anonymous visitors.
              </i18n.Translate>
            </p>
          </div>

          {purpose !== "create" || !userIsAdmin ? undefined : (
            <div class="sm:col-span-5">
              <div class="flex items-center justify-between">
                <span class="flex flex-grow flex-col">
                  <span
                    class="text-sm text-onBackground font-medium leading-6 "
                    id="exchange-account-label"
                  >
                    <i18n.Translate>
                      Does this account belong to a Payment Service Provider?
                    </i18n.Translate>
                  </span>
                </span>
                <button
                  type="button"
                  name="is exchange"
                  data-enabled={
                    (form.isExchange ?? defaultValue.isExchange)
                      ? "true"
                      : "false"
                  }
                  class="bg-primary data-[enabled=false]:bg-gray-200 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  role="switch"
                  aria-checked={!!(form.isExchange ?? defaultValue.isExchange)}
                  aria-labelledby="exchange-account-label"
                  onClick={() => {
                    form.isExchange = !form.isExchange;
                    updateForm(structuredClone(form));
                  }}
                >
                  <span
                    aria-hidden="true"
                    data-enabled={
                      (form.isExchange ?? defaultValue.isExchange)
                        ? "true"
                        : "false"
                    }
                    class="translate-x-5 data-[enabled=false]:translate-x-0 pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  ></span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {children}
    </form>
  );
}

function getAccountId(
  type: "iban" | "x-taler-bank",
  s: Paytos.FullPaytoString | undefined,
): string | undefined {
  if (s === undefined) {
    return undefined;
  }
  const p = Paytos.fromString(s);
  if (p.tag === "error") {
    return undefined;
  }
  if (type === "iban" && p.value.targetType === PaytoType.IBAN) {
    return p.value.iban;
  }
  if (type === "x-taler-bank" && p.value.targetType === PaytoType.TalerBank) {
    return p.value.account;
  }
  return undefined;
}
